/**
 * THE CONSOLE'S ONE RING, its arithmetic (Hamzah, 2026-10-20: "any circle with a number inside").
 * Every ring the console draws is `Ring.tsx` at this one size, this one stroke and this one clear
 * space inside, so the text never touches or crowds the circle; the energy ring, a fuel or a charge
 * level, a compliance score, a donut of work orders are the same object at the same scale.
 *
 * THE TEXT IS LINES, AND A LINE NEVER BREAKS. The big number, then its unit, then the time or the
 * qualifier, smaller and grey: `1,516` / `kWh` / `since 7:15 AM`. The Overview's Energy today ring
 * printed "kWh, since 2:00 / PM", a unit and a time folded into one line that wrapped mid-phrase.
 * Each line is set on one line, and a line too long for the ring's text box is set SMALLER to fit
 * (`fitFont`), down to a floor, never wrapped.
 *
 * Pure: geometry and type in, numbers out, tested without a screen.
 */

/** The ring's outer diameter, px, everywhere. */
export const RING_SIZE = 132;
/** Its stroke, px, everywhere. */
export const RING_STROKE = 10;
/** The clear space between the inside of the stroke and the text, px. */
export const RING_PAD = 10;
/** The circle the stroke is centred on. */
export const RING_R = (RING_SIZE - RING_STROKE) / 2;
export const RING_C = 2 * Math.PI * RING_R;
export const RING_CENTRE = RING_SIZE / 2;
/**
 * The width a line of text may take: the clear disc inside the stroke, less the clear space on
 * both sides, narrowed by a tenth because the top and bottom lines sit off the centre, on a chord
 * shorter than the diameter.
 */
export const RING_TEXT_W = Math.floor((RING_SIZE - 2 * RING_STROKE - 2 * RING_PAD) * 0.9);

/** Each line's size, px, and the smallest it may be set at before it would stop being read. */
export const RING_TYPE = {
  value: { base: 28, min: 14 },
  unit: { base: 13, min: 10 },
  note: { base: 11.5, min: 9 },
} as const;

/** A fraction of the circle as a stroke dash; null for nothing to draw (no reading, or none of it). */
export function arcDash(fraction: number | null | undefined): string | null {
  if (fraction === null || fraction === undefined || !Number.isFinite(fraction) || fraction <= 0) return null;
  const f = Math.min(1, fraction);
  return `${f * RING_C} ${RING_C}`;
}

/** A short mark across the stroke at a fraction of the way round from the top: "usual by now". */
export function tickAt(fraction: number): { x1: number; y1: number; x2: number; y2: number } {
  const a = Math.min(1, Math.max(0, fraction)) * 2 * Math.PI - Math.PI / 2;
  const reach = RING_STROKE * 1.1;
  return {
    x1: RING_CENTRE + (RING_R - reach) * Math.cos(a),
    y1: RING_CENTRE + (RING_R - reach) * Math.sin(a),
    x2: RING_CENTRE + (RING_R + reach) * Math.cos(a),
    y2: RING_CENTRE + (RING_R + reach) * Math.sin(a),
  };
}

/**
 * Segments of a donut, each its share of the whole, laid end to end from the top. `gap` is a clear
 * length left after each slice where there are two or more, so they read as slices, not one band.
 */
export function segmentsOf(values: readonly number[], gap = 0): Array<{ length: number; offset: number }> {
  const total = values.reduce((a, v) => a + Math.max(0, v), 0);
  if (total <= 0) return [];
  const live = values.filter((v) => v > 0).length;
  const cut = live > 1 ? gap : 0;
  let offset = 0;
  return values.map((v) => {
    const full = (Math.max(0, v) / total) * RING_C;
    const s = { length: full > 0 ? Math.max(0.5, full - cut) : 0, offset };
    offset += full;
    return s;
  });
}

/**
 * The size a line is set at: its own size while it fits the text box, else smaller in proportion,
 * never under the floor. `natural` is the line's width set at `base`.
 */
export function fitFont(base: number, natural: number, box: number = RING_TEXT_W, min: number = 0): number {
  if (!(natural > box) || !(box > 0)) return base;
  return Math.max(min, Math.floor(base * (box / natural) * 10) / 10);
}

/** The lines a ring prints, in order, with nothing empty: the number, its unit, its qualifier. */
export function ringLines(o: { value: string; unit?: string | null | undefined; note?: string | null | undefined }): Array<{ kind: 'value' | 'unit' | 'note'; text: string }> {
  const out: Array<{ kind: 'value' | 'unit' | 'note'; text: string }> = [{ kind: 'value', text: o.value.trim() }];
  if (o.unit && o.unit.trim()) out.push({ kind: 'unit', text: o.unit.trim() });
  if (o.note && o.note.trim()) out.push({ kind: 'note', text: o.note.trim() });
  return out;
}
